/**
 * Width-driven queue-layout decision for the operations panel (#211, control-panel
 * spec §2). Replaces the old "portrait ⇒ always side-by-side" assumption: the two
 * protected queues sit side-by-side ONLY when the panel is wide enough for two
 * readable columns; below that they drop to the stacked-tabs fallback (one
 * full-width queue + a blink on the hidden tab). The choice is driven by the
 * panel's MEASURED width (a ResizeObserver), not a device/orientation guess, so it
 * adapts cleanly to any window size.
 *
 * Pure (no React) so the threshold is unit-testable.
 */

export type QueueLayout = 'side-by-side' | 'tabbed';

/** Minimum width a single queue column needs to stay readable (text + badges). */
export const QUEUE_COLUMN_MIN_W = 340;
/** Gap between the two columns — matches the `gap-3` (12px) the layout uses. */
export const QUEUE_COLUMN_GAP = 12;
/**
 * Inner width (content box, padding excluded) needed for two readable columns
 * side-by-side: two columns + the gap between them.
 */
export const SIDE_BY_SIDE_MIN_W = QUEUE_COLUMN_MIN_W * 2 + QUEUE_COLUMN_GAP; // 692
/** Horizontal padding the queue region adds around the columns (`p-3` = 12px ×2). */
export const QUEUE_REGION_PADDING = 24;

/**
 * Pick the queue layout for a measured panel width.
 *
 * Before the first measurement (`width <= 0`) default to side-by-side — it's the
 * common wide case and matches the prior behavior; the ResizeObserver corrects to
 * tabbed within a frame on a narrow window, so there's no sticky wrong state.
 */
export function queueLayoutForWidth(
  panelWidth: number,
  padding: number = QUEUE_REGION_PADDING,
): QueueLayout {
  if (panelWidth <= 0) return 'side-by-side';
  const inner = panelWidth - padding;
  return inner >= SIDE_BY_SIDE_MIN_W ? 'side-by-side' : 'tabbed';
}
